import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { getPartnersByFieldsAndQuery } from 'modules/partners/api/getPartnersByFieldsAndQuery';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

import { Partner } from 'types';

type Props = {
  societyId: string;
};

const fields = 'cif name email sharesCount';

const createPartnerOptions = (partners: Partner[]) =>
  partners.map((partner) => ({
    value: partner?._id,
    label: `${partner?.cif} | ${partner?.name}`,
    name: partner?.name,
    cif: partner?.cif,
    email: partner?.email,
  }));

export const useGetPartnerOptions = ({ societyId }: Props) => {
  const [partnerOptions, setPartnerOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchPartners = useCallback(async () => {
    if (!societyId) return;
    try {
      setIsLoading(true);

      const partners = await store.dispatch(
        getPartnersByFieldsAndQuery(societyId, { fields })
      );

      const options = createPartnerOptions(partners);
      setPartnerOptions(options);

      if (!partners?.length) {
        setPartnerOptions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  }, [societyId]);

  const fetchPartnersByTerm = async (searchTerm: string) => {
    if (!searchTerm) return;
    try {
      setIsLoading(true);

      const partners = await store.dispatch(
        getPartnersByFieldsAndQuery(societyId, { fields, searchTerm })
      );

      const options = createPartnerOptions(partners);
      setPartnerOptions(options);

      return options;
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  return {
    data: { partnerOptions },
    isLoading,
    isError,
    actions: { fetchPartnersByTerm },
  };
};
